import { useIdioma } from "../i18n/IdiomaProvider";
import { ChipSelecao } from "../components/ui/ChipSelecao";
import { IconeCalendario } from "../components/icons";
import {
  DIAS_SEMANA,
  MESES_CURTOS,
  OPCOES_PERIODO,
  anosDaDecada,
  formatarValorCampo,
  mesmaData,
  tituloCalendario,
  type ViewCalendario,
} from "./usePeriodoInterativo";
import type { usePeriodoInterativo } from "./usePeriodoInterativo";
import styles from "./PainelPeriodo.module.css";

type EstadoPeriodo = ReturnType<typeof usePeriodoInterativo>;

interface PainelPeriodoProps {
  estado: EstadoPeriodo;
  className?: string;
  /** Mantém o painel visível (mesmo tamanho), só desativando a interação — usado quando o toggle "Adicionar período" está desligado. */
  desabilitado?: boolean;
}

/** UI pura (chips de atalho + calendário) do seletor de período, sem overlay/header/footer — compartilhada entre FiltroPeriodoModal e o modal de filtros avançados. */
export function PainelPeriodo({ estado, className, desabilitado = false }: PainelPeriodoProps) {
  const { traduzir } = useIdioma();
  const {
    tipo,
    etapaPersonalizada,
    viewCalendario,
    referenciaCalendario,
    rascunhoInicio,
    rascunhoFim,
    resumoPeriodo,
    hoje,
    diasCalendario,
    anosCalendario,
    selecionarTipo,
    navegarCalendario,
    alternarViewCalendario,
    selecionarMesCalendario,
    selecionarAnoCalendario,
    selecionarDiaCalendario,
  } = estado;

  function renderGradeDias() {
    return (
      <>
        <div className={styles.calWeekdays}>
          {DIAS_SEMANA.map((dia) => (
            <span key={dia}>{traduzir(dia)}</span>
          ))}
        </div>
        <div className={styles.calDaysGrid}>
          {diasCalendario.map((dataDia, indice) => {
            const foraMes = dataDia.getMonth() !== referenciaCalendario.getMonth();
            const time = dataDia.getTime();
            const startT = rascunhoInicio ? rascunhoInicio.getTime() : null;
            const endT = rascunhoFim ? rascunhoFim.getTime() : null;
            const emFaixa = startT !== null && endT !== null && time >= startT && time <= endT;
            const rangeStart = startT !== null && time === startT;
            const rangeEnd = endT !== null && time === endT;
            const single = startT !== null && endT === null && time === startT;
            const hojeAtivo = mesmaData(dataDia, hoje);

            return (
              <button
                key={`${dataDia.toISOString()}-${indice}`}
                type="button"
                className={[
                  styles.calDayWrap,
                  foraMes ? styles.vazio : "",
                  emFaixa ? styles.inRange : "",
                  rangeStart ? styles.rangeStart : "",
                  rangeEnd ? styles.rangeEnd : "",
                  single ? styles.singleSelect : "",
                  !foraMes ? styles.interativo : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (foraMes) return;
                  selecionarDiaCalendario(dataDia);
                }}
              >
                <span
                  className={[styles.calDay, foraMes ? styles.otherMonth : "", hojeAtivo ? styles.calHoje : ""]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {dataDia.getDate()}
                </span>
              </button>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div
      className={[styles.corpo, desabilitado ? styles.corpoDesabilitado : "", className].filter(Boolean).join(" ")}
      aria-disabled={desabilitado || undefined}
    >
      <aside className={styles.painelLateral}>
        <div className={styles.chipsField}>
          <span className={styles.periodoLabel}>{traduzir("Selecione um intervalo rápido")}</span>
          <div className={styles.chipsLista} role="listbox" aria-label={traduzir("Selecionar período")}>
            {OPCOES_PERIODO.map((opcao) => (
              <ChipSelecao
                key={opcao.tipo}
                role="option"
                aria-selected={opcao.tipo === tipo}
                selecionado={opcao.tipo === tipo}
                tamanho="compacto"
                mostrarIndicadorSelecao
                className={styles.chipPeriodo}
                onClick={() => selecionarTipo(opcao.tipo)}
              >
                {opcao.titulo}
              </ChipSelecao>
            ))}
          </div>
        </div>

        {tipo === "personalizado" && (
          <p className={styles.personalizadoHint}>
            {rascunhoInicio && !rascunhoFim
              ? `${traduzir("Início selecionado")}: ${formatarValorCampo(rascunhoInicio)}. ${traduzir("Escolha a data final no calendário.")}`
              : traduzir("Use o calendário para escolher o início e o fim do período.")}
          </p>
        )}
      </aside>

      <section className={styles.calendarWrapper}>
        {/* Só o resumo do intervalo, centralizado: os campos de data repetiam
           o que o calendário já mostra. */}
        {resumoPeriodo && (
          <div className={styles.camposData}>
            <span className={styles.campoDataResumo}>
              <IconeCalendario className={styles.campoDataIcone} aria-hidden="true" />
              {resumoPeriodo}
            </span>
          </div>
        )}

        <div className={styles.calHeader}>
          <button type="button" className={styles.calNavBtn} onClick={() => navegarCalendario(-1)}>
            &#10094;
          </button>

          <button type="button" className={styles.calTitle} onClick={alternarViewCalendario}>
            {tituloCalendario(viewCalendario as ViewCalendario, referenciaCalendario, traduzir)}
          </button>

          <button type="button" className={styles.calNavBtn} onClick={() => navegarCalendario(1)}>
            &#10095;
          </button>
        </div>

        {viewCalendario === "dias" && renderGradeDias()}

        {viewCalendario === "meses" && (
          <div className={styles.gridMeses}>
            {MESES_CURTOS.map((mes, indice) => (
              <button
                key={mes}
                type="button"
                className={`${styles.itemMesAno} ${indice === referenciaCalendario.getMonth() ? styles.itemMesAnoAtivo : ""}`}
                onClick={() => selecionarMesCalendario(indice)}
              >
                {traduzir(mes)}
              </button>
            ))}
          </div>
        )}

        {viewCalendario === "anos" && (
          <div className={styles.gridMeses}>
            {anosCalendario.map((ano) => {
              const baseDecada = Math.floor(referenciaCalendario.getFullYear() / 10) * 10;
              const fora = ano < baseDecada || ano > baseDecada + 9;

              return (
                <button
                  key={ano}
                  type="button"
                  className={`${styles.itemMesAno} ${
                    ano === referenciaCalendario.getFullYear() ? styles.itemMesAnoAtivo : ""
                  } ${fora ? styles.anoFora : ""}`}
                  onClick={() => selecionarAnoCalendario(ano)}
                >
                  {ano}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
